// Preview bounded five-field cron schedules in the selected business timezone.
// This is configuration only: browser timers must never impersonate a backend scheduler.
const readAdminFlowCronField = (field: string, min: number, max: number): Set<number> => {
  const values = new Set<number>();

  // The installed node-cron applies a step to the entire field, not to each item in a list.
  // Keep this preview subset unambiguous instead of displaying a schedule the backend would interpret differently.
  if (field.includes(",") && field.includes("/"))
    throw new Error("Use a list or a stepped range in each cron field, rather than combining them.");

  field.split(",").forEach((part) => {
    if (!/^(\*|\d+(-\d+)?)(\/\d+)?$/.test(part))
      throw new Error("Use numbers, ranges, lists, * and / steps in the five cron fields.");

    const [range, stepText] = part.split("/");
    const step = stepText === undefined ? 1 : Number(stepText);
    const ends = range === "*" ? [min, max] : range.split("-").map(Number);
    const start = ends[0];
    const end = ends[1] ?? start;

    if (!Number.isInteger(step) || step < 1 || step > max - min + 1 || start < min || end > max || end < start)
      throw new Error("A cron field is outside its allowed range.");

    // node-cron v3 filters expanded values by divisibility, including fields starting at one.
    for (let value = start; value <= end; value += 1) {
      if (stepText === undefined || value % step === 0) values.add(value);
    }
  });

  if (!values.size) throw new Error("This cron field selects no values. Use * or a range before a / step.");

  return values;
};

export const previewAdminFlowSchedule = (cron: string, timezone: string, now = new Date()): string[] => {
  const fields = cron.trim().split(/\s+/);

  if (fields.length !== 5) throw new Error("Enter five cron fields: minute hour day month weekday.");

  if (!Number.isFinite(now.getTime())) throw new Error("The schedule preview needs a valid starting time.");

  // node-cron converts Sunday 7 to 0 before expanding ranges.
  // Accept the standalone alias, but require 0 through 6 for predictable ranges and steps.
  if (fields[4].split(",").some((part) => part !== "7" && /(^|\D)7(\D|$)/.test(part)))
    throw new Error("Use 0 for Sunday when writing weekday ranges or steps.");

  fields[4] = fields[4]
    .split(",")
    .map((part) => (part === "7" ? "0" : part))
    .join(",");

  const [minutes, hours, days, months, weekdays] = fields.map((field, index) =>
    readAdminFlowCronField(field, [0, 0, 1, 1, 0][index], [59, 23, 31, 12, 6][index]),
  );
  const clock = new Intl.DateTimeFormat("en-US", {
    timeZone: timezone,
    minute: "numeric",
    hour: "numeric",
    hourCycle: "h23",
    day: "numeric",
    month: "numeric",
    weekday: "short",
  });
  const display = new Intl.DateTimeFormat("en-GB", {
    timeZone: timezone,
    weekday: "short",
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
    timeZoneName: "short",
  });
  const results: string[] = [];
  const start = Math.floor(now.getTime() / 60000) * 60000 + 60000;

  for (let offset = 0; offset < 60 * 24 * 14 && results.length < 5; offset += 1) {
    const date = new Date(start + offset * 60000);
    const parts = Object.fromEntries(clock.formatToParts(date).map((part) => [part.type, part.value]));
    const weekday = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].indexOf(parts.weekday);
    const weekdayMatch = weekdays.has(weekday);
    const dayMatch = days.has(Number(parts.day));
    // The backend's node-cron requires both calendar fields, unlike Unix cron's restricted-field OR rule.
    const calendarMatch = dayMatch && weekdayMatch;

    if (
      minutes.has(Number(parts.minute)) &&
      hours.has(Number(parts.hour)) &&
      months.has(Number(parts.month)) &&
      calendarMatch
    )
      results.push(display.format(date));
  }

  return results;
};
